import { Box, makeStyles, Paper, Typography } from "@material-ui/core";

import React from "react";
import { Control, Controller, useWatch, useController } from "react-hook-form";
import { InformationRequest } from "../../../domain/InformationRequest";
import { LookupTracker } from "@ofqual/portal-core/core/types";
import { ToggleButton } from "./ToggleButton";
import { AddStaffButtonAndDialog } from "./AddStaffButtonAndDialog";
import { PermissionsList } from "./PermissionsList";
import { Skeleton } from "@material-ui/lab";
import { useGetTeamsAndUsers } from "../../../hooks/useGetTeamsAndUsers";

const useStyles = makeStyles((theme) => ({
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

export type PermissionsProps = {
  control: Control<InformationRequest>;
  lookupTracker: LookupTracker;
};

export const Permissions: React.FC<PermissionsProps> = ({
  control,
  lookupTracker,
}) => {
  const classes = useStyles();

  const {
    visibleToAllStaff = false,
    createdByUpn,
  } = useWatch<InformationRequest>({
    control,
    name: ["visibleToAllStaff", "createdByUpn", "persmissions"],
  });

  const { userLookups, teamLookups, users } = useGetTeamsAndUsers();

  // need to have permissions manipulation at this level as both the "Add Staff" button
  //  and the Permissions List (which removes users) interact with the permissions property
  const {
    field: { onChange: permissionsOnChange, value: permissionsValue },
  } = useController({
    name: "permissions",
    control,
  });

  const typedPermissionsValue = (permissionsValue ||
    []) as InformationRequest["permissions"];

  const handleAddTeamOrUser = (id: string) => {
    const foundUser = users.find((user) => user.upn === id)?.upn;

    const usersToAdd = foundUser
      ? [foundUser] // a single user has been selected
      : users // a team has been selected
          .filter((user) => user.userTeams.some((team) => team.id === id))
          .map((user) => user.upn);

    const newUsersToAdd = usersToAdd.filter(
      (userToAdd) => userToAdd && !typedPermissionsValue.includes(userToAdd)
    );

    permissionsOnChange([...typedPermissionsValue, ...newUsersToAdd]);
  };

  const handleRemoveUser = (userIdToRemove: string) => {
    permissionsOnChange(
      typedPermissionsValue.filter((id) => id !== userIdToRemove)
    );
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <Box flexGrow={1}>
          <Typography variant="h6">Ofqual Permissions</Typography>
        </Box>
        <Box>
          <Controller
            control={control}
            name="visibleToAllStaff"
            render={({ value, onChange }) => (
              <ToggleButton value={value} onChange={onChange} />
            )}
          />
          &nbsp; &nbsp;
        </Box>
        {!visibleToAllStaff ? (
          <Box>
            <AddStaffButtonAndDialog
              disabled={visibleToAllStaff}
              lookupTracker={lookupTracker}
              userLookups={userLookups}
              teamLookups={teamLookups}
              onSelect={handleAddTeamOrUser}
            />
          </Box>
        ) : null}
      </Box>

      <Paper className={classes.formSection}>
        {users.length ? (
          <PermissionsList
            ownerUpn={createdByUpn}
            permissions={permissionsValue}
            users={users}
            visibleToAllStaff={visibleToAllStaff}
            onDelete={handleRemoveUser}
          />
        ) : (
          <div>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
          </div>
        )}
      </Paper>
    </>
  );
};
